if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.addEventListener('submit', this.onSubmit.bind(this));
        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          this.render();
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      /*
       * URL de la page de resultats, cadree sur le modele memorise.
       *
       * `q = <termes> AND title:<marque>` : le ET porte UNIQUEMENT sur la marque. Scoper
       * aussi les termes de l'internaute (`title:chat AND title:noir`) applique un ET strict
       * mot a mot qui effondre la recall — 0 resultat la ou la recherche libre en rend 444.
       * Sous cette forme les termes restent cherches normalement (titre, tags, description)
       * et seule la marque est contrainte :
       *   noel                        -> 68 resultats, toutes marques
       *   noel AND title:iPhone       ->  8 resultats, tous iPhone
       *   chat noir AND title:iPhone  -> 444 resultats, tous iPhone
       *
       * Sans modele memorise on ne touche a rien : la recherche reste globale.
       */
      searchUrlFor(terms) {
        const token = this.phoneModelTitleToken(this.stripBrandScope(terms));
        const cleaned = this.stripBrandScope(terms);
        if (!cleaned) return null;

        const url = new URL(theme.routes.search_url, window.location.origin);
        url.searchParams.set('q', token ? `${cleaned} AND title:${token}` : cleaned);
        url.searchParams.set('type', 'product,article,page');
        url.searchParams.set('options[prefix]', 'last');
        return url.toString();
      }

      // Retire un cadrage marque deja present, pour ne pas empiler ` AND title:X AND title:Y `
      // quand on relance une recherche depuis la page de resultats.
      stripBrandScope(terms) {
        return (terms || '').split(' AND title:')[0].trim();
      }

      onSubmit(event) {
        const url = this.searchUrlFor(this.getQuery());
        if (!url) return;

        event.preventDefault();
        window.location.assign(url);
      }

      /*
       * Jeton de marque tel qu'il apparait dans les TITRES produit, deduit du modele
       * memorise (cookie `phone_model`).
       *
       * C'est le titre qui porte la marque ("Coque iPhone Panda Bubble", "Coque Samsung
       * Fleur legere"), pas un champ structure. Le nom de marque du metaobjet convient
       * partout sauf pour Apple, dont aucun titre ne contient "Apple" : verifie sur les 16
       * marques du menu, `title:chat AND title:apple` renvoie 0 la ou `title:iphone` en
       * renvoie 19. D'ou l'alias, seul cas particulier.
       *
       * Pas de jeton quand la recherche vise un AUTRE telephone (« samsung a73 », « a73 »
       * avec un iPhone memorise) : cadrer sur iPhone n'y rendrait rien. La detection, qui
       * s'appuie sur le catalogue des modeles, vit dans le selecteur de modele.
       */
      phoneModelTitleToken(terms = this.getQuery()) {
        const selector = document.getElementById('PhoneModelSelector');
        if (selector?.queryTargetsOtherBrand?.(terms)) return '';

        const match = document.cookie.match('(^|;)\\s*phone_model\\s*=\\s*([^;]+)');
        if (!match) return '';
        try {
          const model = JSON.parse(decodeURIComponent(match[2]));
          const marque = (model && model.marque ? model.marque : '').toString().trim();
          if (!marque) return '';

          const slug = marque
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase();

          return slug === 'apple' ? 'iPhone' : marque;
        } catch {
          return '';
        }
      }

      buildUrl(brandTerm = '', types = 'collection,product,article,page') {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        // Suggest : on accole le jeton de marque aux termes, SANS `title:`. Le scoping par
        // champ annule le prefixe (`options[prefix]=last`) — `title:cha` ne remonte rien
        // pendant la frappe la ou `cha` remonte 10 suggestions. La page de resultats, elle,
        // n'a plus besoin du prefixe : elle utilise le ET strict (cf. searchUrlFor).
        const query = brandTerm ? `${this.getQuery()} ${brandTerm}` : this.getQuery();

        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', types);
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      render() {
        const brandTerm = this.phoneModelTitleToken();
        if (!brandTerm) {
          this.renderSection(this.buildUrl().toString());
          return;
        }

        this.renderSection(this.buildUrl(brandTerm).toString(), () => {
          // Repli : si le jeton de marque ne renvoie aucun produit, on relance la requete non
          // biaisee plutot que d'afficher une liste vide. Le filtre marque cote client
          // (phone-model-selector) masque ensuite les autres marques, donc le resultat final
          // reste cadre sur le modele memorise — comme la page de resultats.
          const container = document.getElementById('PredictiveSearchResults-' + theme.utils.sectionId(this));
          if (container && !container.querySelector('.horizontal-product')) {
            this.renderSection(this.buildUrl().toString());
            return;
          }

          this.injectUnbiasedCollections();
        });
      }

      // Les titres de collection ne portent pas le jeton de marque (« Coque Samsung Galaxy A74 »,
      // « Coques design fleurs ») : la requete biaisee n'en remonte quasiment jamais. On les
      // redemande sans le jeton et on les greffe avant les produits.
      injectUnbiasedCollections() {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const query = this.getQuery();
        const url = this.buildUrl('', 'collection').toString();

        const inject = (responseText) => {
          // La frappe a pu continuer pendant la requete : on n'injecte que pour les termes courants.
          if (this.getQuery() !== query) return;

          const target = document.getElementById(id);
          if (!target || target.querySelector('[data-search-collections]')) return;

          const block = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector(`#${id} [data-search-collections]`);
          if (!block) return;

          const products = target.querySelector('[data-search-products]');
          products ? products.before(block) : target.prepend(block);
        };

        if (this.cachedMap.has(url)) {
          inject(this.cachedMap.get(url));
          return;
        }

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            this.cachedMap.set(url, responseText);
            inject(responseText);
          })
          .catch((error) => console.error(error));
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();
        this.render();
      }

      renderSection(url, onRendered) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, onRendered)
          : this.renderSectionFromFetch(url, onRendered);
      }

      renderSectionFromCache(url, onRendered) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
        onRendered?.();
      }

      renderSectionFromFetch(url, onRendered) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
            onRendered?.();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
